import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      label: "Visit Us",
      value: "Near park kechi, Block FD,\nDakshini Pitampura, Pitampura,\nNew Delhi, Delhi, 110034",
    },
    {
      icon: Mail,
      label: "Email Us",
      value: "hello@iidas.in",
      href: "mailto:hello@iidas.in",
    },
    {
      icon: Phone,
      label: "Call Us",
      value: "+91 9625858007",
      href: "tel:+919625858007",
    },
    {
      icon: Clock,
      label: "Working Hours",
      value: "Mon - Fri: 9:00 AM - 6:00 PM",
    },
  ];

  return (
    <div data-testid="contact-page" className="bg-[#F4F4F0] pt-24">
      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-6"
          >
            Contact Us
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-[#1A1A1A] leading-[0.9] mb-8"
          >
            Let's discuss
            <br />
            your project.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl font-light text-[#1A1A1A]/70 leading-relaxed max-w-2xl"
          >
            Have a project in mind? We'd love to hear about it. Reach out to us and let's start a conversation.
          </motion.p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          {/* Contact Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="space-y-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-3"
                  >
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name-input"
                    className="w-full bg-transparent border-b border-[#1A1A1A]/20 px-0 py-4 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none transition-colors placeholder:text-[#1A1A1A]/40"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-3"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email-input"
                    className="w-full bg-transparent border-b border-[#1A1A1A]/20 px-0 py-4 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none transition-colors placeholder:text-[#1A1A1A]/40"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-3"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    data-testid="contact-phone-input"
                    className="w-full bg-transparent border-b border-[#1A1A1A]/20 px-0 py-4 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none transition-colors placeholder:text-[#1A1A1A]/40"
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div>
                  <label
                    htmlFor="subject"
                    className="block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-3"
                  >
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    data-testid="contact-subject-input"
                    className="w-full bg-transparent border-b border-[#1A1A1A]/20 px-0 py-4 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none transition-colors placeholder:text-[#1A1A1A]/40"
                    placeholder="New Residential Project"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-3"
                >
                  Your Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  data-testid="contact-message-input"
                  className="w-full bg-transparent border-b border-[#1A1A1A]/20 px-0 py-4 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none transition-colors resize-none placeholder:text-[#1A1A1A]/40"
                  placeholder="Tell us about your project..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
                className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F4F4F0] px-8 py-4 uppercase tracking-widest text-xs hover:bg-[#3A4D39] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send Message
                    <Send size={16} />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-5">
            <div className="space-y-10">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-4"
                  data-testid={`contact-info-${index}`}
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-[#1A1A1A]/20 flex-shrink-0">
                    <item.icon size={20} className="text-[#3A4D39]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-2">
                      {item.label}
                    </p>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-[#1A1A1A] hover:text-[#3A4D39] transition-colors whitespace-pre-line"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-[#1A1A1A] whitespace-pre-line">
                        {item.value}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Map Placeholder */}
            <div className="mt-16 aspect-square bg-[#E5E5E0] border border-[#1A1A1A]/10 flex items-center justify-center">
              <div className="text-center">
                <MapPin size={32} className="text-[#1A1A1A]/30 mx-auto mb-4" />
                <p className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/40">
                  Map Location
                </p>
                <p className="text-sm text-[#1A1A1A]/60 mt-2">
                  Pitampura, New Delhi
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
